import { useTheme } from '@/context/ThemeContext';
import { WikiArticle } from '@/context/WikiContext';
import { useBadgeVisibility } from '@/context/BadgeVisibilityContext';

interface Props {
  articles: WikiArticle[];
  onSelect: (id: string) => void;
}

const COMPARE_ROWS = [
  { label: 'Cycle Life', keys: { lifepo4: '2000+', agm: '400-600', gel: '500-800' } },
  { label: 'Depth of Discharge', keys: { lifepo4: '80%', agm: '50%', gel: '50%' } },
  { label: 'Design Life', keys: { lifepo4: '10 years', agm: '3-5 years', gel: '4-6 years' } },
  { label: 'Weight', keys: { lifepo4: 'Lightest', agm: 'Heavy', gel: 'Heavy' } },
  { label: 'Maintenance', keys: { lifepo4: 'None', agm: 'None', gel: 'None' } },
  { label: 'Charger Required', keys: { lifepo4: 'Lithium-specific', agm: 'Standard', gel: 'Gel-specific' } },
  { label: 'Best For', keys: { lifepo4: 'Offshore / Pro', agm: 'Recreational', gel: 'Electronics / Solar' } },
  { label: 'Price Point', keys: { lifepo4: 'Premium', agm: 'Mid-range', gel: 'Mid-range' } },
];

export default function WikiComparisonTable({ articles, onSelect }: Props) {
  const { theme } = useTheme();
  const { settings } = useBadgeVisibility();
  const light = theme === 'light';

  const published = articles.filter((a) => a.published);

  const text = light ? 'text-gray-900' : 'text-white';
  const muted = light ? 'text-gray-500' : 'text-white/50';
  const card = light ? 'bg-white border border-gray-100' : 'bg-[#1C2B3A] border border-white/5';
  const rowAlt = light ? 'bg-gray-50' : 'bg-white/3';

  return (
    <div className={`${card} rounded-2xl overflow-hidden`}>
      <div className="p-6 border-b border-white/5">
        <h2 className={`font-black text-lg ${text}`}>
          <i className="ri-table-line mr-2" style={{ color: 'var(--accent)' }}></i>
          Side-by-Side Comparison
        </h2>
        <p className={`text-xs mt-1 ${muted}`}>Click any technology to explore it in detail</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className={light ? 'bg-gray-50' : 'bg-white/5'}>
              <th className={`text-left px-6 py-4 text-xs font-bold uppercase tracking-widest ${muted}`}>Feature</th>
              {published.map((a) => (
                <th key={a.id} className="px-6 py-4 text-center">
                  <button
                    onClick={() => onSelect(a.id)}
                    className="flex flex-col items-center gap-1.5 mx-auto cursor-pointer group"
                  >
                    <div
                      className="w-9 h-9 flex items-center justify-center rounded-xl text-white transition-transform group-hover:scale-110"
                      style={{ background: a.badgeColor }}
                    >
                      <i className={`${a.icon} text-base`}></i>
                    </div>
                    <span className={`text-xs font-black ${text} group-hover:underline`}>{a.title}</span>
                    {settings.wikiBadges && (
                      <span
                        className="text-xs font-bold px-2 py-0.5 rounded-full text-white"
                        style={{ background: a.badgeColor }}
                      >
                        {a.badge}
                      </span>
                    )}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {COMPARE_ROWS.map((row, i) => (
              <tr key={i} className={i % 2 === 0 ? '' : rowAlt}>
                <td className={`px-6 py-3.5 text-xs font-semibold ${muted} whitespace-nowrap`}>{row.label}</td>
                {published.map((a) => {
                  const val = (row.keys as Record<string, string>)[a.id] || '—';
                  return (
                    <td key={a.id} className="px-6 py-3.5 text-center">
                      <span className={`text-xs font-bold ${text}`}>{val}</span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
